/**
 * DayPicker - a month calendar you tap days on
 * ---------------------------------------------
 * Used when posting a chore that runs over more than one day: "walk the dog on
 * Monday, Wednesday and Friday, because a neighbour covers the other two".
 *
 * WHY THIS IS HAND-WRITTEN AND NOT A LIBRARY
 *
 * A date picker is the classic thing to install rather than build, and this
 * one is about a hundred lines. The three off-the-shelf ones considered all
 * brought a date library with them, and this project deliberately has neither -
 * the frontend's whole dependency list is React, the router, axios and the
 * icons. Adding two packages, which then need updating for ever, to draw a
 * grid of numbers is a bad trade on a site one person maintains.
 *
 * WHAT IT DOES NOT DO, ON PURPOSE
 *
 * No range dragging, no keyboard grid navigation beyond ordinary tabbing. Every
 * day is a real <button>, so a screen reader announces it and the keyboard
 * reaches it, which is the part that matters. Dragging a range would be nice
 * and is also the first thing to break on a touchscreen.
 *
 * TIME ZONES ARE THE WHOLE POINT OF DOING THIS IN THE BROWSER. The dates that
 * come out of here are built from the poster's own clock, so "5pm on the 20th"
 * means five in the afternoon where they are standing. The server could not
 * work that out - see the long note at the top of backend/utils/visits.js.
 */

import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useI18n } from '../hooks/useI18n';

/**
 * MONTH AND WEEKDAY NAMES COME FROM THE LOCALE, not from a list here and not
 * from the dictionaries.
 *
 * A calendar needs twelve month names and seven weekday initials per language,
 * and every one of them is something the browser already knows. Putting them
 * in i18n/en.js and i18n/it.js would be nineteen more phrases to translate,
 * to arrive at "agosto" - which `toLocaleDateString('it-IT')` returns for
 * free, correctly capitalised for Italian, and would go on returning for a
 * third language nobody has translated yet.
 *
 * Built from a known Monday so the week starts on Monday, which is how a week
 * is written in Italy and everywhere else this site is used.
 */
function monthNames(locale) {
  return Array.from({ length: 12 }, (_, m) =>
    new Date(2021, m, 1).toLocaleDateString(locale, { month: 'long' }));
}

function weekdayInitials(locale) {
  // 4 January 2021 was a Monday.
  return Array.from({ length: 7 }, (_, i) =>
    new Date(2021, 0, 4 + i)
      .toLocaleDateString(locale, { weekday: 'narrow' })
      .toUpperCase());
}

/** '2026-08-20' for a Date - a stable key that ignores the time of day. */
export function dayKey(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Midnight today, for comparing "is this day in the past". */
function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * The days to draw for one month, padded so the 1st lands under the right
 * weekday. Monday-first, because that is how a week is written in Italy and
 * everywhere else this site is used - JavaScript's getDay() puts Sunday at 0,
 * hence the shuffle.
 */
function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const leading = (first.getDay() + 6) % 7;
  const days = [];

  for (let i = 0; i < leading; i += 1) days.push(null);

  const cursor = new Date(year, month, 1);
  while (cursor.getMonth() === month) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

/**
 * @param selected  array of '2026-08-20' keys
 * @param onToggle  called with the key when a day is tapped
 * @param maxDate   nothing after this can be picked (the 3-month horizon)
 */
export default function DayPicker({ selected = [], onToggle, maxDate = null }) {
  const { t, locale } = useI18n();
  const today = startOfToday();
  const months = monthNames(locale);
  const initials = weekdayInitials(locale);
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });

  const days = monthGrid(view.year, view.month);
  const chosen = new Set(selected);

  // A functional update, so two taps in quick succession move two months.
  // Reading `view` from the closure instead means both taps compute from the
  // same starting month and the second one is thrown away - which reads as the
  // arrow being unresponsive rather than as a bug.
  const step = (by) => {
    setView((v) => {
      const d = new Date(v.year, v.month + by, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  // Do not let somebody page back into months that are entirely gone, or
  // forward past the furthest date they are allowed to pick. A calendar that
  // scrolls for ever suggests you can book for ever.
  const canGoBack = new Date(view.year, view.month, 1) > new Date(today.getFullYear(), today.getMonth(), 1);
  const canGoForward = !maxDate
    || new Date(view.year, view.month + 1, 1) <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  return (
    <div className="daypicker">
      <div className="daypicker-head">
        <button
          type="button"
          className="daypicker-nav"
          onClick={() => step(-1)}
          disabled={!canGoBack}
          aria-label={t('daypicker.previousMonth')}
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>

        {/* aria-live so that paging the month is announced - otherwise a
            screen reader user hears nothing change and the grid silently
            becomes a different month underneath them. */}
        <span className="daypicker-month" aria-live="polite">
          {months[view.month]} {view.year}
        </span>

        <button
          type="button"
          className="daypicker-nav"
          onClick={() => step(1)}
          disabled={!canGoForward}
          aria-label={t('daypicker.nextMonth')}
        >
          <ChevronRight size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="daypicker-grid" role="group" aria-label={t('daypicker.chooseDays')}>
        {initials.map((letter, i) => (
          // The initials repeat (T/T, S/S), so the key is the position. The
          // heading is aria-hidden because "M T W T F S S" read aloud is
          // noise - each day button already says its own full date.
          <span key={`h${i}`} className="daypicker-weekday" aria-hidden="true">
            {letter}
          </span>
        ))}

        {days.map((date, i) => {
          if (!date) return <span key={`pad${i}`} className="daypicker-pad" />;

          const key = dayKey(date);
          const isPast = date < today;
          const isBeyond = maxDate && date > maxDate;
          const isChosen = chosen.has(key);

          return (
            <button
              key={key}
              type="button"
              className={`daypicker-day${isChosen ? ' is-chosen' : ''}`}
              disabled={isPast || isBeyond}
              aria-pressed={isChosen}
              // The visible label is just the number, which on its own means
              // nothing out of context. This is what actually gets read.
              aria-label={date.toLocaleDateString(locale, {
                weekday: 'long', day: 'numeric', month: 'long',
              })}
              onClick={() => onToggle(key)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
